import React from "react";
import { FiPaperclip, FiSend } from "react-icons/fi";
import { MessageInputProps } from "../../types/messages.types";
import { UI_TEXT, FILE_UPLOAD } from "../../constants/messages.constants";
import FilePreview from "./FilePreview";

/**
 * MessageInput Component
 * Input field with file attachment and send button
 */
const MessageInput: React.FC<MessageInputProps> = ({
  message,
  attachments,
  filePreviews,
  selectedChatId,
  isUploading,
  onMessageChange,
  onSend,
  onFileChange,
  onRemovePreview,
  fileInputRef,
}) => {
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 px-4 sm:px-6 py-3 sm:py-4 border-t border-neutral-200 dark:border-neutral-700">
      {/* File Previews */}
      {selectedChatId && filePreviews[selectedChatId]?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 p-2 bg-neutral-50 dark:bg-neutral-900 rounded-xl">
          {filePreviews[selectedChatId].map((preview, index) => (
            <FilePreview
              key={index}
              preview={preview}
              index={index}
              chatId={selectedChatId}
              onRemove={onRemovePreview}
            />
          ))}
        </div>
      )}

      {/* Input Row */}
      <div className="flex items-end gap-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-3 text-neutral-600 hover:text-[#F56948] dark:text-neutral-400 dark:hover:text-[#F56948] hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-xl transition-all"
          aria-label="Attach file"
          disabled={isUploading}
        >
          <FiPaperclip size={20} />
        </button>

        <div className="flex-1 relative">
          <input
            type="text"
            value={message}
            onChange={(e) => onMessageChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={UI_TEXT.PLACEHOLDERS.MESSAGE}
            disabled={isUploading}
            className="w-full bg-neutral-100 dark:bg-neutral-700 border-2 border-neutral-200 dark:border-neutral-600 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F56948] focus:border-[#F56948] text-neutral-900 dark:text-white placeholder-neutral-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={onFileChange}
          className="hidden"
          accept={FILE_UPLOAD.ACCEPTED_TYPES}
        />

        <button
          onClick={onSend}
          disabled={
            isUploading || (!message.trim() && attachments.length === 0)
          }
          className="bg-[#F56948] text-white px-5 py-3 rounded-2xl hover:bg-[#E5593D] focus:ring-2 focus:ring-offset-2 focus:ring-[#F56948] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
        >
          {isUploading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span className="hidden sm:inline">
                {UI_TEXT.BUTTONS.SENDING}
              </span>
            </>
          ) : (
            <>
              <FiSend size={18} />
              <span className="hidden sm:inline">{UI_TEXT.BUTTONS.SEND}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MessageInput;
